/**
 * Чипи фільтра в панелі списку (E4a-W4; FLT-10, FLT-11, FLT-18) — частина першого екрана: діюча умова
 * видно одразу, без лінивого чанка. Меню полів, поповер чипа, бічна панель і конструктор — лінивий шар
 * `tools/FilterLayer.tsx`: вантажиться на намір (наведення, фокус, клік, `F`) чи в простої.
 *
 * - **Чип на умову** (корінь `and` без груп): «Поле: значення», клік — поповер умови, «✕» — прибрати
 *   з тостом «Скасувати». Корінь `or` чи групи — один чип «Розширений фільтр · N умов», клік —
 *   бічна панель (FLT-11).
 * - **Переповнення**: чипи, що не вмістились у рядок, ховаються за «+N», а «+N» відкриває панель;
 *   перший чип стискається з трикрапкою, а тісно й для стиснутого — усі за «+N». Мінімум рядка — від
 *   складу (`rowMinimum`), а не від показаних чипів.
 * - **Клавіші**: `←` / `→` між чипами, `Enter` — змінити, `Delete` / `Backspace` — прибрати, `F` на
 *   екрані — «+ Фільтр».
 *
 * Що зміна означає для подання (чернетка в адресі чи повернення до умови подання), вирішує екран
 * (`onChange`): цей модуль лише показує й змінює дерево.
 */
import type { FilterNode, MetaEntity } from '@clientline/contracts';
import { UNDO_TTL_MS } from '@clientline/contracts/undo-window';
import {
  type KeyboardEvent,
  lazy,
  type ReactNode,
  Suspense,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useHotkeys } from '../../app/hotkeys.ts';
import { common } from '../../i18n/uk/common.ts';
import { filters as t } from '../../i18n/uk/filters.ts';
import type { MenuItem } from '../../ui/Menu.tsx';
import { toast } from '../../ui/notify/store.ts';
import { RetryBoundary } from '../../ui/RetryBoundary.tsx';
import { forgetUndo, rememberUndo } from '../undo-last.ts';
import { chipsOf, chipText, conditionCount, conditionsOf } from './summary.ts';
import type { FilterLayerOpen } from './tools/FilterLayer.tsx';
import { withCondition } from './tree-edit.ts';

/** Лінивий шар. Імпорт повторюється на кожну спробу: відхилений браузер не кешує. */
export const loadFilterLayer = () => import('./tools/FilterLayer.tsx');
const layerChunk = () => lazy(loadFilterLayer);

export interface FilterBarProps {
  entity: MetaEntity;
  /** Діюче дерево: чернетка людини, а без неї — умова подання; `null` — без умови. */
  filter: FilterNode | null;
  onChange: (next: FilterNode | null) => void;
  /** Сутність-ціль поля-зв'язку з `/meta`. */
  targetOf: (key: string) => MetaEntity | undefined;
  /** Наведення в легкому режимі нічого не вантажить (lite-mode.md). */
  lite: boolean;
}

export interface FilterBar {
  /** Чипи умов і «+ Фільтр» — у `.toolbar__filters`. */
  chips: ReactNode;
  /** «Фільтр · N» — у `.toolbar__actions`: бічна панель. */
  button: ReactNode;
  /** Та сама дія пунктом «⋯», коли кнопка не вміщується в рядок дій (ViewToolbar). */
  menuItem: Extract<MenuItem, { kind: 'item' }>;
  /** Меню, поповер чи панель — під панеллю екрана. */
  layer: ReactNode;
  /** Завантажити шар заздалегідь (простій після першого екрана). */
  preload: () => void;
}

/** Відступ між чипами (`--cl-space-1`) і запас під «+N» (з відступом). */
const GAP = 4;
const MORE_RESERVE = 44;
/** Найвужчий текст стиснутого чипа (трикрапка після 2–3 літер), якщо повний текст ширший. */
const BODY_MIN = 48;

/**
 * Чи елемент має місце на екрані: не під `display: none` (свого чи предка). Без
 * `checkVisibility` (jsdom) — так: розкладки там немає, і «+ Фільтр» вважаємо показаним.
 */
function rendered(el: HTMLElement): boolean {
  return typeof el.checkVisibility === 'function' ? el.checkVisibility() : true;
}

/**
 * Ширина чипа без стиснення: єдиний видимий чип стискається з трикрапкою, коли рядок вузький
 * (поруч «• змінено»), а замір рахує за повним текстом — інакше він коливався б.
 */
function naturalWidth(chip: HTMLElement): number {
  const body = chip.querySelector<HTMLElement>('.chip__body');
  return chip.offsetWidth + (body ? body.scrollWidth - body.clientWidth : 0);
}

/**
 * Мінімум рядка чипів фільтра: поля й рамка, «+ Фільтр» і запас під «+N», коли є чипи умов (тісно —
 * усі вони йдуть у «+N»). Розширений фільтр — один чип без «+N»: замість запасу — сам чип, стиснутий
 * до трикрапки (рамка, «✕» і `BODY_MIN` тексту, але не ширше за повний текст).
 */
function rowMinimum(box: HTMLElement, add: number, conditions: number): number {
  const s = getComputedStyle(box);
  const edges = [s.paddingLeft, s.paddingRight, s.borderLeftWidth, s.borderRightWidth]
    .map((v) => Number.parseFloat(v) || 0)
    .reduce((sum, v) => sum + v, 0);
  if (conditions > 0) return Math.ceil(edges + add + MORE_RESERVE);
  const chip = box.querySelector<HTMLElement>('.chip--filter');
  return Math.ceil(edges + add + (chip ? GAP + squeezedWidth(chip) : 0));
}

/** Ширина чипа, стиснутого до трикрапки: рамка, «✕» і `BODY_MIN` тексту (не ширше за повний). */
function squeezedWidth(chip: HTMLElement): number {
  const body = chip.querySelector<HTMLElement>('.chip__body');
  if (!body) return chip.offsetWidth;
  return chip.offsetWidth - body.offsetWidth + Math.min(BODY_MIN, body.scrollWidth);
}

export function useFilterBar({
  entity,
  filter,
  onChange,
  targetOf,
  lite,
}: FilterBarProps): FilterBar {
  const [open, setOpen] = useState<FilterLayerOpen | null>(null);
  const [Layer, setLayer] = useState(layerChunk);
  const anchor = useRef<HTMLElement | null>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLButtonElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  /** Тост «Скасувати» живе довше за рендер: остання версія обробника екрана. */
  const latest = useRef(onChange);
  latest.current = onChange;

  const chips = chipsOf(filter);
  const conditions = chips.kind === 'simple' ? chips.conditions : [];
  const total = conditionsOf(filter).length;
  const texts = conditions.map((c) => chipText(entity, c));

  const asked = useRef(false);
  const preload = useCallback(() => {
    if (asked.current) return;
    asked.current = true;
    loadFilterLayer().catch(() => {
      asked.current = false;
    });
  }, []);
  const intent = lite ? undefined : preload;

  const show = useCallback((next: FilterLayerOpen, from: HTMLElement | null) => {
    anchor.current = from;
    setOpen(next);
  }, []);
  const openAdd = useCallback(() => {
    // Розширений фільтр чипами не доповнюється: умову додають у конструкторі. Рядок чипів схований
    // (робоча область < 600, `shell.css`): «+ Фільтр» без місця на екрані — тоді теж бічна панель
    // від «Фільтр · N», а не меню від нульового прямокутника (QA #902).
    const add = addRef.current;
    if (chips.kind === 'advanced' || !add || !rendered(add)) {
      show({ kind: 'panel' }, panelRef.current);
    } else show({ kind: 'add' }, add);
  }, [chips.kind, show]);

  useHotkeys([{ code: 'KeyF', label: t.addFilterLabel, handler: openAdd }]);

  // ---- переповнення: скільки чипів умов вміщується в рядок
  const [fit, setFit] = useState<number>(Number.POSITIVE_INFINITY);
  // Мінімум рядка (`min-width`): `null` — не заміряно (jsdom, прихований рядок).
  const [minWidth, setMinWidth] = useState<number | null>(null);
  const signature = [chips.kind, chips.kind === 'advanced' ? chips.count : '', ...texts].join(
    '\u0001',
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: перемірювати, коли змінився склад рядка
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const width = box.clientWidth;
      const add = addRef.current?.offsetWidth ?? 0;
      const items = [...box.querySelectorAll<HTMLElement>('[data-filter-chip]')];
      // Сталий мінімум — від складу рядка, а не від показаних чипів (рядок має `contain:
      // inline-size`, QC #902): «+ Фільтр» з полями і «+N» (`rowMinimum`).
      setMinWidth((prev) => {
        const next = add > 0 ? rowMinimum(box, add, items.length) : null;
        return prev === next ? prev : next;
      });
      // jsdom і прихований контейнер: ширин немає — показуємо все.
      if (width === 0 || items.length === 0) return setFit(Number.POSITIVE_INFINITY);
      let used = add + GAP;
      let count = 0;
      for (const item of items) {
        const next = used + naturalWidth(item) + GAP;
        const reserve = count + 1 < items.length ? MORE_RESERVE : 0;
        if (next + reserve > width) break;
        used = next;
        count += 1;
      }
      // Повністю не вміщується й один чип — він стискається з трикрапкою, якщо є місце на стиснутий;
      // немає — усі в «+N»: її місце тримає мінімум рядка.
      const first = items[0];
      if (count === 0 && first) {
        const reserve = items.length > 1 ? MORE_RESERVE : 0;
        if (add + GAP + squeezedWidth(first) + GAP + reserve <= width) count = 1;
      }
      setFit(count >= items.length ? Number.POSITIVE_INFINITY : count);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [signature]);
  const hidden = Number.isFinite(fit) ? Math.max(0, conditions.length - fit) : 0;

  const focusChip = (index: number) => {
    const bodies = boxRef.current?.querySelectorAll<HTMLElement>('[data-chip-nav]:not([hidden])');
    const list = bodies ? [...bodies].filter((el) => !el.closest('[inert]')) : [];
    (list[Math.max(0, Math.min(index, list.length - 1))] ?? addRef.current)?.focus();
  };

  /** Локальна дія: без сервера, але в тому самому стеку `Ctrl+Z` (audit-undo.md, розд. 6). */
  const offerLocalUndo = (before: typeof filter, text: string) => {
    const back = () => {
      forgetUndo(back);
      latest.current(before);
    };
    rememberUndo(back, UNDO_TTL_MS);
    toast({ text, action: { label: common.undo, onAction: back }, key: 'filter-removed' });
  };

  const remove = (index: number) => {
    const before = filter;
    const label = texts[index] ?? '';
    latest.current(withCondition(filter, index, null));
    offerLocalUndo(before, t.filterRemoved(label));
    requestAnimationFrame(() => focusChip(index));
  };

  const removeAll = () => {
    const before = filter;
    latest.current(null);
    offerLocalUndo(before, t.filterRemovedAll);
    requestAnimationFrame(() => addRef.current?.focus());
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const navs = [
      ...(boxRef.current?.querySelectorAll<HTMLElement>('[data-chip-nav]') ?? []),
    ].filter((el) => !el.closest('[inert]'));
    const at = navs.indexOf(target);
    if (at < 0) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      navs[(at + (e.key === 'ArrowRight' ? 1 : -1) + navs.length) % navs.length]?.focus();
    } else if ((e.key === 'Delete' || e.key === 'Backspace') && target.dataset.chipIndex) {
      e.preventDefault();
      const index = Number(target.dataset.chipIndex);
      if (index < 0) removeAll();
      else remove(index);
    }
  };

  const chipsNode = (
    // biome-ignore lint/a11y/useSemanticElements: група чипів (ARIA), не поле форми
    <div
      ref={boxRef}
      className={chips.kind === 'none' ? 'filter-chips filter-chips--empty' : 'filter-chips'}
      style={minWidth === null ? undefined : { minWidth }}
      role="group"
      aria-label={t.chipsLabel}
      onKeyDown={onKeyDown}
      data-testid="filter-chips"
    >
      {chips.kind === 'advanced' && (
        <span className="chip chip--active chip--filter" data-testid="filter-chip">
          <button
            type="button"
            className="chip__body"
            aria-haspopup="dialog"
            aria-label={t.editFilter(t.advancedChip(conditionCount(chips.count)))}
            data-chip-nav
            data-chip-index={-1}
            onPointerEnter={intent}
            onFocus={preload}
            onClick={(e) => show({ kind: 'panel' }, e.currentTarget)}
          >
            {t.advancedChip(conditionCount(chips.count))}
          </button>
          <button
            type="button"
            className="chip__x"
            aria-label={t.removeFilter(t.advancedChip(conditionCount(chips.count)))}
            title={t.removeFilter(t.advancedChip(conditionCount(chips.count)))}
            tabIndex={-1}
            onClick={removeAll}
          >
            <i className="ic ic-close" aria-hidden="true" />
          </button>
        </span>
      )}
      {conditions.map((condition, i) => {
        const text = texts[i] ?? '';
        const over = i >= conditions.length - hidden;
        return (
          <span
            // Ключ — позиція: зміна значення не перемонтовує чип і не губить фокус.
            // biome-ignore lint/suspicious/noArrayIndexKey: умова не має власного id
            key={i}
            className={`chip chip--active chip--filter${over ? ' chip--overflow' : ''}`}
            data-filter-chip
            data-testid="filter-chip"
            {...(over ? { inert: true, 'aria-hidden': true } : {})}
            title={text}
          >
            <button
              type="button"
              className="chip__body"
              aria-haspopup="dialog"
              aria-label={t.editFilter(text)}
              data-chip-nav
              data-chip-index={i}
              data-field={condition.field}
              onPointerEnter={intent}
              onFocus={preload}
              onClick={(e) => show({ kind: 'edit', index: i }, e.currentTarget)}
            >
              {text}
            </button>
            <button
              type="button"
              className="chip__x"
              aria-label={t.removeFilter(text)}
              title={t.removeFilter(text)}
              tabIndex={-1}
              onClick={() => remove(i)}
            >
              <i className="ic ic-close" aria-hidden="true" />
            </button>
          </span>
        );
      })}
      {hidden > 0 && (
        <button
          type="button"
          className="chip"
          aria-haspopup="dialog"
          aria-label={t.moreChipsLabel(hidden, texts.slice(conditions.length - hidden).join('; '))}
          data-chip-nav
          onPointerEnter={intent}
          onFocus={preload}
          onClick={(e) => show({ kind: 'panel' }, e.currentTarget)}
          data-testid="filter-more"
        >
          {t.moreChips(hidden)}
        </button>
      )}
      <button
        ref={addRef}
        type="button"
        className="chip chip--add"
        aria-haspopup="dialog"
        aria-label={t.addFilterLabel}
        aria-keyshortcuts="F"
        aria-expanded={open?.kind === 'add'}
        data-chip-nav
        onPointerEnter={intent}
        onFocus={preload}
        onClick={openAdd}
        data-testid="filter-add"
      >
        + {t.addFilter}
      </button>
    </div>
  );

  const button = (
    <button
      ref={panelRef}
      type="button"
      className={`btn btn--ghost${total > 0 ? ' filter-open--on' : ''}`}
      aria-haspopup="dialog"
      aria-expanded={open?.kind === 'panel'}
      title={t.filterButtonHint}
      onPointerEnter={intent}
      onFocus={preload}
      onClick={(e) => show({ kind: 'panel' }, e.currentTarget)}
      data-testid="filter-panel-open"
    >
      <i className="ic ic-filter" aria-hidden="true" />
      {total > 0 ? t.filterCount(total) : t.filterButton}
    </button>
  );

  const layer = open && (
    <RetryBoundary
      onRetry={() => setLayer(() => layerChunk())}
      fallback={() => (
        <p className="list-tail list-tail--error" role="alert">
          {t.toolsFailed}
        </p>
      )}
    >
      <Suspense fallback={null}>
        <Layer
          // Інший стан — свіжий шар: поповер іншого чипа не бере чужої чернетки.
          key={open.kind === 'edit' ? `edit:${open.index}` : open.kind}
          open={open}
          entity={entity}
          filter={filter}
          onChange={(next) => latest.current(next)}
          onClose={() => setOpen(null)}
          anchor={anchor}
          targetOf={targetOf}
        />
      </Suspense>
    </RetryBoundary>
  );

  const menuItem: FilterBar['menuItem'] = {
    kind: 'item',
    id: 'filter-panel',
    label: total > 0 ? t.filterCount(total) : t.filterButton,
    onSelect: () => show({ kind: 'panel' }, panelRef.current),
  };

  return { chips: chipsNode, button, menuItem, layer, preload };
}
